import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Icon } from '@/components/ui/icon';
import { useAuthStore, isBctStaff } from '@/stores/auth';

// ============================================================================
// Types
// ============================================================================

interface WelcomeWizardProps {
  open: boolean;
  onComplete: () => void;
}

// ============================================================================
// Constants
// ============================================================================

const TOTAL_STEPS = 4;

const KEY_ACTIONS = [
  {
    icon: 'bug_report',
    titleKey: 'welcome.actions.reportIssues.title',
    descKey: 'welcome.actions.reportIssues.description',
  },
  {
    icon: 'assignment_turned_in',
    titleKey: 'welcome.actions.runTests.title',
    descKey: 'welcome.actions.runTests.description',
  },
  {
    icon: 'description',
    titleKey: 'welcome.actions.viewDocuments.title',
    descKey: 'welcome.actions.viewDocuments.description',
  },
  {
    icon: 'trending_up',
    titleKey: 'welcome.actions.trackProgress.title',
    descKey: 'welcome.actions.trackProgress.description',
  },
] as const;

const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 80 : -80,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -80 : 80,
    opacity: 0,
  }),
};

// ============================================================================
// Step Components
// ============================================================================

function StepWelcome({
  t,
  userName,
  isAdmin,
}: {
  t: (key: string, opts?: Record<string, string>) => string;
  userName: string;
  isAdmin: boolean;
}) {
  return (
    <div className="flex flex-col items-center text-center px-2 py-4">
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
        <Icon name="waving_hand" className="h-8 w-8 text-primary" />
      </div>
      <h3 className="text-xl font-semibold">{t('welcome.greeting', { name: userName })}</h3>
      <p className="mt-3 text-sm text-muted-foreground leading-relaxed max-w-sm">
        {t('welcome.subtitle')}
      </p>
      {isAdmin && <p className="mt-3 text-sm font-medium text-primary">{t('welcome.adminNote')}</p>}
    </div>
  );
}

function StepKeyActions({ t }: { t: (key: string) => string }) {
  return (
    <div className="px-2 py-2">
      <h3 className="mb-4 text-center text-lg font-semibold">{t('welcome.keyActionsTitle')}</h3>
      <div className="grid grid-cols-2 gap-3">
        {KEY_ACTIONS.map((action) => (
          <div
            key={action.icon}
            className="flex flex-col items-center gap-2 rounded-lg border border-border bg-muted/30 p-4 text-center"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
              <Icon name={action.icon} className="h-5 w-5 text-primary" />
            </div>
            <span className="text-sm font-medium">{t(action.titleKey)}</span>
            <span className="text-xs text-muted-foreground leading-snug">{t(action.descKey)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function StepTips({ t }: { t: (key: string) => string }) {
  const tips = [
    { icon: 'help', key: 'welcome.tips.help' },
    { icon: 'route', key: 'welcome.tips.tours' },
    { icon: 'support_agent', key: 'welcome.tips.support' },
  ] as const;

  return (
    <div className="px-2 py-2">
      <h3 className="mb-4 text-center text-lg font-semibold">{t('welcome.tipsTitle')}</h3>
      <div className="space-y-3">
        {tips.map((tip) => (
          <div
            key={tip.icon}
            className="flex items-start gap-3 rounded-lg border border-border bg-muted/30 p-4"
          >
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10">
              <Icon name={tip.icon} className="h-4 w-4 text-primary" />
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed pt-1">{t(tip.key)}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function StepStart({ t, onComplete }: { t: (key: string) => string; onComplete: () => void }) {
  return (
    <div className="flex flex-col items-center text-center px-2 py-6">
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
        <Icon name="rocket_launch" className="h-8 w-8 text-primary" />
      </div>
      <p className="mb-6 text-sm text-muted-foreground leading-relaxed max-w-sm">
        {t('welcome.subtitle')}
      </p>
      <Button variant="primary" size="lg" onClick={onComplete}>
        {t('welcome.startExploring')}
      </Button>
    </div>
  );
}

// ============================================================================
// Step Indicators
// ============================================================================

function StepIndicators({ current, total }: { current: number; total: number }) {
  return (
    <div className="flex items-center justify-center gap-1.5">
      {Array.from({ length: total }, (_, i) => (
        <div
          key={i}
          className={`h-2 rounded-full transition-all duration-300 ${
            i === current ? 'w-6 bg-primary' : 'w-2 bg-muted-foreground/30'
          }`}
        />
      ))}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function WelcomeWizard({ open, onComplete }: WelcomeWizardProps) {
  const { t } = useTranslation('help');
  const user = useAuthStore((s) => s.user);
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1);

  const userName = user?.name?.split(' ')[0] || '';
  const isAdmin = user?.role === 'CUSTOMER_ADMIN';
  const shouldHide = !!user && isBctStaff(user.role);

  const handleNext = useCallback(() => {
    if (step < TOTAL_STEPS - 1) {
      setDirection(1);
      setStep((s) => s + 1);
    }
  }, [step]);

  const handleBack = useCallback(() => {
    if (step > 0) {
      setDirection(-1);
      setStep((s) => s - 1);
    }
  }, [step]);

  const handleComplete = useCallback(() => {
    onComplete();
    // Reset for potential re-open
    setStep(0);
    setDirection(1);
  }, [onComplete]);

  if (shouldHide) return null;

  const renderStep = () => {
    switch (step) {
      case 0:
        return <StepWelcome t={t} userName={userName} isAdmin={isAdmin} />;
      case 1:
        return <StepKeyActions t={t} />;
      case 2:
        return <StepTips t={t} />;
      case 3:
        return <StepStart t={t} onComplete={handleComplete} />;
      default:
        return null;
    }
  };

  const isLastStep = step === TOTAL_STEPS - 1;
  const isFirstStep = step === 0;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleComplete()}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="sr-only">{t('welcome.greeting', { name: userName })}</DialogTitle>
          <DialogDescription className="sr-only">
            {t('welcome.stepOf', { current: String(step + 1), total: String(TOTAL_STEPS) })}
          </DialogDescription>
        </DialogHeader>

        {/* Step content with animation */}
        <div className="min-h-[280px] overflow-hidden">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={step}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.25, ease: 'easeInOut' }}
            >
              {renderStep()}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Step indicators */}
        <StepIndicators current={step} total={TOTAL_STEPS} />

        {/* Navigation */}
        <div className="flex items-center justify-between pt-2">
          <div>
            {!isFirstStep && (
              <Button variant="ghost" size="sm" onClick={handleBack}>
                {t('welcome.back')}
              </Button>
            )}
          </div>
          <span className="text-xs text-muted-foreground">
            {t('welcome.stepOf', { current: String(step + 1), total: String(TOTAL_STEPS) })}
          </span>
          <div>
            {!isLastStep && (
              <Button size="sm" onClick={handleNext}>
                {t('welcome.next')}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
